import { TOTALS_KEY } from "constants/tableConversion"
import {
  EngineSigCell,
  RTableColumnFormatDetails,
  RTableContent,
} from "types/engineOutput.types"
import {
  DTAB_ONLY_STATS,
  resolveStatFormat,
  STATISTIC_LABEL,
  XTAB_ONLY_STATS,
} from "./statRegistry"
import {
  Axis,
  AxisLeaf,
  CellSig,
  CellSigLetter,
  HeaderNode,
  ModelCell,
  SigElement,
  SigMeta,
  TableModel,
} from "./tableModel.types"

type RawCell = string | number | null
type FormatMap = Record<string, RTableColumnFormatDetails>
type LevelName = "upper" | "lower"

const FREEZE_PANES = "freeze panes"
const PATH_SEPARATOR = "//"

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const cellAt = (data: RawCell[][], x: number, y: number): RawCell => {
  if (x < 0 || y < 0) return null
  const value = data[y]?.[x]
  return value === undefined ? null : value
}

const isBlank = (value: RawCell): boolean => value === null || value === ""

const asLabel = (value: RawCell): string =>
  value === null || value === undefined ? "" : String(value)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const toFiniteNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((item) => String(item)) : []

/** Parses an engine "x,y" key; an empty part (whole row/column/table) gives null. */
const parseCoordKey = (
  key: string
): { x: number | null; y: number | null } | null => {
  const parts = key.split(",")
  if (parts.length !== 2) return null
  const parsePart = (part: string): number | null | undefined => {
    const trimmed = part.trim()
    if (trimmed === "") return null
    return /^\d+$/.test(trimmed) ? Number(trimmed) : undefined
  }
  const x = parsePart(parts[0])
  const y = parsePart(parts[1])
  if (x === undefined || y === undefined) return null
  return { x, y }
}

// ---------------------------------------------------------------------------
// Origin and statistic axis
// ---------------------------------------------------------------------------

/** First data cell: the "freeze panes" key, else one right/below the "Statistic" cell. */
const findOrigin = (
  data: RawCell[][],
  format: FormatMap
): { x: number; y: number } => {
  for (const [key, props] of Object.entries(format)) {
    if (!isRecord(props) || props[FREEZE_PANES] !== true) continue
    const coord = parseCoordKey(key)
    if (coord && coord.x !== null && coord.y !== null) {
      return { x: coord.x, y: coord.y }
    }
  }
  for (let y = 0; y < data.length; y++) {
    const row = data[y] ?? []
    for (let x = 0; x < row.length; x++) {
      if (row[x] === STATISTIC_LABEL) return { x: x + 1, y: y + 1 }
    }
  }
  return { x: 0, y: 0 }
}

const hasNumberProp = (props: unknown): boolean =>
  isRecord(props) && typeof props.number === "string"

/** Stats run down when a whole-row key (",y") carries "number", across for "x,". */
const findStatAxis = (format: FormatMap): "down" | "across" => {
  let across = false
  for (const [key, props] of Object.entries(format)) {
    if (!hasNumberProp(props)) continue
    const coord = parseCoordKey(key)
    if (!coord) continue
    if (coord.x === null && coord.y !== null) return "down"
    if (coord.x !== null && coord.y === null) across = true
  }
  return across ? "across" : "down"
}

/** Number hint with cell > row > column > table precedence; undefined when none. */
const resolveNumberHint = (
  format: FormatMap,
  x: number,
  y: number
): string | undefined => {
  for (const key of [`${x},${y}`, `,${y}`, `${x},`, ","]) {
    const props = format[key]
    if (hasNumberProp(props)) return (props as { number: string }).number
  }
  return undefined
}

/** Stat positions per header element: the distance to the first repeat of the first stat. */
const findStatPeriod = (stats: string[]): number => {
  if (stats.length === 0) return 1
  for (let i = 1; i < stats.length; i++) {
    if (stats[i] === stats[0]) return i
  }
  return stats.length
}

// ---------------------------------------------------------------------------
// Header reconstruction
// ---------------------------------------------------------------------------

/**
 * Rebuilds full header levels for one element from the engine's sparse,
 * bottom-aligned header cells. The engine writes only the levels that differ
 * from the previous element, so a blank level inherits from the previous
 * element. The exception is the single-level Totals leaf, which the engine
 * writes at the innermost level only and which has no parents.
 */
const resolveLevels = (
  raw: RawCell[],
  prev: (string | null)[],
  isLastElement: boolean,
  allowTotals: boolean
): (string | null)[] => {
  const depth = raw.length
  const top = raw.findIndex((value) => !isBlank(value))
  if (top === -1) return [...prev]
  if (
    allowTotals &&
    isLastElement &&
    top === depth - 1 &&
    asLabel(raw[depth - 1]) === TOTALS_KEY
  ) {
    return [...Array(depth - 1).fill(null), TOTALS_KEY]
  }
  return [
    ...prev.slice(0, top),
    ...raw.slice(top).map((value) => (isBlank(value) ? null : asLabel(value))),
  ]
}

interface AxisSource {
  /** Header cells per position, level 0 = outermost. */
  headerCells: (position: number) => RawCell[]
  positions: number
  levels: number
  /** Stat label per position when this is the stat axis. */
  stats?: string[]
}

const buildLeaves = (source: AxisSource, allowTotals: boolean): AxisLeaf[] => {
  const { positions, levels, stats } = source
  const period = stats ? findStatPeriod(stats) : 1
  const elementCount = Math.ceil(positions / period)
  const leaves: AxisLeaf[] = []
  let prev: (string | null)[] = Array(levels).fill(null)

  for (let element = 0; element < elementCount; element++) {
    const start = element * period
    const resolved = resolveLevels(
      source.headerCells(start),
      prev,
      element === elementCount - 1,
      allowTotals
    )
    prev = resolved
    const path = resolved.filter((label): label is string => label !== null)
    const isTotal =
      allowTotals &&
      element === elementCount - 1 &&
      path.length === 1 &&
      path[0] === TOTALS_KEY
    const end = Math.min(start + period, positions)
    for (let position = start; position < end; position++) {
      leaves.push({
        id: path.join(PATH_SEPARATOR),
        path: [...path],
        ...(stats ? { stat: stats[position] } : {}),
        elementIndex: element,
        isTotal,
      })
    }
  }
  return leaves
}

/** Prefix tree over consecutive leaves; a leaf-level node spans all stat leaves of its element. */
const buildTree = (
  leaves: AxisLeaf[],
  start: number,
  end: number,
  level: number
): HeaderNode[] => {
  const nodes: HeaderNode[] = []
  let i = start
  while (i < end) {
    const leaf = leaves[i]
    if (leaf.path.length <= level) {
      // Only reachable at level 0 for an element without any header label.
      let j = i + 1
      while (
        j < end &&
        leaves[j].path.length <= level &&
        leaves[j].elementIndex === leaf.elementIndex
      ) {
        j++
      }
      if (level === 0) nodes.push({ label: "", span: [i, j], children: [] })
      i = j
      continue
    }
    const label = leaf.path[level]
    const endsHere = leaf.path.length === level + 1
    let j = i + 1
    while (j < end) {
      const next = leaves[j]
      if (next.path[level] !== label) break
      if (endsHere) {
        if (
          next.path.length !== level + 1 ||
          next.elementIndex !== leaf.elementIndex
        ) {
          break
        }
      } else if (next.path.length <= level + 1) {
        break
      }
      j++
    }
    nodes.push({
      label,
      span: [i, j],
      children: endsHere ? [] : buildTree(leaves, i, j, level + 1),
    })
    i = j
  }
  return nodes
}

/**
 * The header tree over `leaves`, as `buildTableModel` builds it: consecutive
 * leaves share a node while their paths agree, and a leaf-level node spans
 * the statistic leaves of one element. For models derived from a built one
 * (rows reordered, dropped or added).
 */
export const buildHeaderTree = (leaves: AxisLeaf[]): HeaderNode[] =>
  buildTree(leaves, 0, leaves.length, 0)

const makeAxis = (leaves: AxisLeaf[]): Axis => ({
  depth: leaves.reduce((max, leaf) => Math.max(max, leaf.path.length), 0),
  leaves,
  tree: buildHeaderTree(leaves),
})

// ---------------------------------------------------------------------------
// Significance
// ---------------------------------------------------------------------------

interface ParsedSignificance {
  meta: SigMeta
  cells: Record<string, EngineSigCell>
}

/** Validates the untrusted block and converts spans to banner-axis leaf indices. */
const parseSignificance = (
  block: unknown,
  origin: { x: number; y: number },
  statAxis: "down" | "across",
  kind: TableModel["kind"]
): ParsedSignificance | undefined => {
  if (!isRecord(block) || !isRecord(block.banner)) return undefined
  const banner = block.banner
  if (!Array.isArray(banner.elements)) return undefined
  const settings = isRecord(block.settings) ? block.settings : {}

  const bannerAxis: "across" | "down" =
    banner.axis === "rows"
      ? "down"
      : banner.axis === "columns"
        ? "across"
        : kind === "DTAB"
          ? statAxis
          : statAxis === "down"
            ? "across"
            : "down"
  const offset = bannerAxis === "across" ? origin.x : origin.y

  // Cells refer to elements by index, so a malformed entry keeps its place as an
  // empty element (no span, no letter) instead of shifting the ones after it.
  const elements: SigElement[] = banner.elements.map(
    (entry: unknown): SigElement => {
      if (!isRecord(entry)) {
        return {
          span: [0, 0],
          header: [],
          letter: null,
          group: null,
          baseN: 0,
          lowBase: false,
          total: false,
        }
      }
      const element = entry
      const span = Array.isArray(element.span) ? element.span : []
      const start = toFiniteNumber(span[0]) ?? offset
      const end = toFiniteNumber(span[1]) ?? start
      const baseEff = toFiniteNumber(element.base_eff)
      return {
        span: [start - offset, end - offset] as [number, number],
        header: toStringArray(element.header),
        letter: typeof element.letter === "string" ? element.letter : null,
        group: toFiniteNumber(element.group) ?? null,
        baseN: toFiniteNumber(element.base_n) ?? 0,
        ...(baseEff !== undefined ? { baseEff } : {}),
        lowBase: element.low_base === true,
        total: element.total === true,
      }
    }
  )

  const groups = Array.isArray(banner.groups)
    ? banner.groups.filter(isRecord).map((group) => ({
        label: String(group.label ?? ""),
        letters: String(group.letters ?? ""),
        overlap: group.overlap === true,
      }))
    : []

  const levels = Array.isArray(settings.levels)
    ? settings.levels.filter(
        (level): level is number =>
          typeof level === "number" && Number.isFinite(level)
      )
    : []

  // Missing settings fall back to the SIG defaults (overlap on, REST off, min base 30).
  const meta: SigMeta = {
    levels,
    rest: settings.rest === true,
    overlap: settings.overlap !== false,
    minBase: toFiniteNumber(settings.min_base) ?? 30,
    weighted: settings.weighted === true,
    bannerAxis,
    elements,
    groups,
    legend: toStringArray(block.legend),
    notes: toStringArray(block.notes),
  }
  const cells = isRecord(block.cells)
    ? (block.cells as Record<string, EngineSigCell>)
    : {}
  return { meta, cells }
}

/** Banner element paths from the significance block replace the reconstructed ones. */
const applySigPaths = (leaves: AxisLeaf[], elements: SigElement[]): void => {
  elements.forEach((element, index) => {
    const from = Math.max(0, element.span[0])
    const to = Math.min(leaves.length, element.span[1])
    for (let i = from; i < to; i++) {
      leaves[i] = {
        ...leaves[i],
        id: element.header.join(PATH_SEPARATOR),
        path: [...element.header],
        elementIndex: index,
        isTotal: element.total,
      }
    }
  })
}

const levelName = (levels: number[], level: unknown): LevelName =>
  levels.length > 1 && level === levels[1] ? "lower" : "upper"

const letterCase = (letter: string, level: LevelName): string =>
  level === "upper" ? letter.toUpperCase() : letter.toLowerCase()

const toCellSig = (entry: EngineSigCell, levels: number[]): CellSig => {
  const skip = typeof entry.skip === "string" ? entry.skip : undefined
  // An entry that does not say which element it beats cannot be shown or
  // explained, so it is dropped rather than pointed at a made-up element.
  const letters: CellSigLetter[] = Array.isArray(entry.higher_than)
    ? entry.higher_than.filter(isRecord).flatMap((higher) => {
        const element = toFiniteNumber(higher.element)
        if (element === undefined || element < 0) return []
        const level = levelName(levels, higher.level)
        const p = toFiniteNumber(higher.p)
        return [
          {
            letter: letterCase(String(higher.letter ?? ""), level),
            level,
            element,
            ...(p !== undefined ? { p } : {}),
          },
        ]
      })
    : []
  const sig: CellSig = {
    tested: skip === undefined,
    ...(skip !== undefined ? { skip } : {}),
    lowBase:
      typeof entry.low_base === "boolean"
        ? entry.low_base
        : skip === "low_base",
    letters,
  }
  const n = toFiniteNumber(entry.n)
  if (n !== undefined) sig.n = n
  // Why the rest-of-sample comparison was skipped: kept only on a tested cell
  // (an untested cell already says why it has no result) and only as a
  // non-empty string.
  const restSkip = entry.rest_skip as unknown
  if (sig.tested && typeof restSkip === "string" && restSkip !== "") {
    sig.restSkip = restSkip
  }
  // A vs-rest result without a valid direction is dropped: guessing "up" would
  // show an arrow the engine never computed.
  const vsRest = entry.vs_rest
  if (isRecord(vsRest) && (vsRest.dir === "up" || vsRest.dir === "down")) {
    const p = toFiniteNumber(vsRest.p)
    sig.vsRest = {
      dir: vsRest.dir,
      level: levelName(levels, vsRest.level),
      ...(p !== undefined ? { p } : {}),
    }
  }
  return sig
}

const attachSigCells = (
  cells: ModelCell[][],
  sigCells: Record<string, EngineSigCell>,
  origin: { x: number; y: number },
  levels: number[]
): void => {
  /** Model position of an engine "x,y" key, when it names a data cell. */
  const positionOf = (key: unknown): { row: number; col: number } | undefined => {
    if (typeof key !== "string") return undefined
    const coord = parseCoordKey(key)
    if (!coord || coord.x === null || coord.y === null) return undefined
    const row = coord.y - origin.y
    const col = coord.x - origin.x
    return cells[row]?.[col] ? { row, col } : undefined
  }
  for (const [key, entry] of Object.entries(sigCells)) {
    if (!isRecord(entry)) continue
    const at = positionOf(key)
    const lettersAt = positionOf(entry.letters_at)
    if (at) {
      const sig = toCellSig(entry, levels)
      if (lettersAt) sig.lettersAt = lettersAt
      cells[at.row][at.col].sig = sig
    }
    if (lettersAt) cells[lettersAt.row][lettersAt.col].role = "letters"
  }
}

// ---------------------------------------------------------------------------
// Public builder
// ---------------------------------------------------------------------------

const hasAny = (stats: Set<string>, list: readonly string[]): boolean =>
  list.some((stat) => stats.has(stat))

/**
 * Builds a typed TableModel from an engine rtable `content`.
 * @param content engine `content` ({data, format, significance?})
 * @param title display title; defaults to the universe label the engine
 *   writes in the top-left corner (UNIVERSE fan-out), if any
 */
export const buildTableModel = (
  content: RTableContent & { significance?: unknown },
  title?: string
): TableModel => {
  const data: RawCell[][] = Array.isArray(content?.data) ? content.data : []
  const format: FormatMap = isRecord(content?.format)
    ? (content.format as FormatMap)
    : {}

  const origin = findOrigin(data, format)
  const statAxis = findStatAxis(format)
  const width = data.reduce(
    (max, row) => Math.max(max, Array.isArray(row) ? row.length : 0),
    0
  )
  const columns = Math.max(0, width - origin.x)
  const rows = Math.max(0, data.length - origin.y)

  // Stat labels sit in the column left of the origin (down) or the row above it (across).
  const stats =
    statAxis === "down"
      ? Array.from({ length: rows }, (_, r) =>
          asLabel(cellAt(data, origin.x - 1, origin.y + r))
        )
      : Array.from({ length: columns }, (_, c) =>
          asLabel(cellAt(data, origin.x + c, origin.y - 1))
        )
  const statSet = new Set(stats.filter((stat) => stat !== ""))
  const statKind: TableModel["kind"] = hasAny(statSet, XTAB_ONLY_STATS)
    ? "XTAB"
    : hasAny(statSet, DTAB_ONLY_STATS)
      ? "DTAB"
      : "unknown"
  const allowTotals = statKind !== "DTAB"

  // Across headers occupy the rows above the data (minus the stat row when stats run across);
  // down headers occupy the columns left of the data (minus the stat column when stats run down).
  const acrossLevels = Math.max(0, statAxis === "across" ? origin.y - 1 : origin.y)
  const downLevels = Math.max(0, statAxis === "down" ? origin.x - 1 : origin.x)

  const acrossLeaves = buildLeaves(
    {
      positions: columns,
      levels: acrossLevels,
      headerCells: (c) =>
        Array.from({ length: acrossLevels }, (_, level) =>
          cellAt(data, origin.x + c, level)
        ),
      ...(statAxis === "across" ? { stats } : {}),
    },
    allowTotals
  )
  const downLeaves = buildLeaves(
    {
      positions: rows,
      levels: downLevels,
      headerCells: (r) =>
        Array.from({ length: downLevels }, (_, level) =>
          cellAt(data, level, origin.y + r)
        ),
      ...(statAxis === "down" ? { stats } : {}),
    },
    allowTotals
  )

  const hasTotal = [...acrossLeaves, ...downLeaves].some((leaf) => leaf.isTotal)
  const kind: TableModel["kind"] =
    statKind !== "unknown"
      ? statKind
      : (statSet.has("n") || statSet.has("N")) && hasTotal
        ? "XTAB"
        : "unknown"

  const cells: ModelCell[][] = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: columns }, (_, c) => {
      const x = origin.x + c
      const y = origin.y + r
      const stat = statAxis === "down" ? stats[r] : stats[c]
      const resolved = resolveStatFormat(stat, resolveNumberHint(format, x, y))
      const cell: ModelCell = {
        raw: cellAt(data, x, y),
        stat,
        kind: resolved.kind,
        format: resolved.format,
      }
      if (resolved.kind === "letters") cell.role = "letters"
      return cell
    })
  )

  const parsedSig = parseSignificance(
    content?.significance,
    origin,
    statAxis,
    kind
  )
  if (parsedSig) {
    const bannerLeaves =
      parsedSig.meta.bannerAxis === "across" ? acrossLeaves : downLeaves
    applySigPaths(bannerLeaves, parsedSig.meta.elements)
    attachSigCells(cells, parsedSig.cells, origin, parsedSig.meta.levels)
  }

  // The engine writes the UNIVERSE element label in the top-left corner; nothing
  // else but the "Statistic" label lives left of and above the origin.
  const corner = cellAt(data, 0, 0)
  const cornerTitle =
    origin.x > 0 &&
    origin.y > 0 &&
    typeof corner === "string" &&
    corner !== "" &&
    corner !== STATISTIC_LABEL
      ? corner
      : undefined
  const resolvedTitle = title ?? cornerTitle

  return {
    ...(resolvedTitle !== undefined ? { title: resolvedTitle } : {}),
    kind,
    statAxis,
    across: makeAxis(acrossLeaves),
    down: makeAxis(downLeaves),
    cells,
    freeze: { x: origin.x, y: origin.y },
    ...(parsedSig ? { significance: parsedSig.meta } : {}),
  }
}

/**
 * A one-cell model holding a message ("No data available"), for a table that
 * has nothing to show or export but should still get its place (e.g. a sheet).
 */
export const messageTableModel = (message: string): TableModel =>
  buildTableModel({ data: [[message]], format: {} })
